import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, ArrowLeft, User } from 'lucide-react';
import { SEO } from '../components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

const formatDate = (dateStr) => {
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch { return dateStr; }
};

const BlogDetail = () => {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/blogs/${id}`)
      .then(r => {
        if (!r.ok) throw new Error('Not found');
        return r.json();
      })
      .then(data => { setBlog(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDF8F0] flex items-center justify-center pt-24" data-testid="blog-loading">
        <div className="animate-pulse text-[#7a6555]">Loading article...</div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen bg-[#FDF8F0] flex flex-col items-center justify-center pt-24 gap-4" data-testid="blog-not-found">
        <h2 className="text-2xl font-bold text-[#2C1810]" style={{ fontFamily: 'Playfair Display, serif' }}>Article Not Found</h2>
        <p className="text-[#7a6555]">The article you're looking for doesn't exist or has been removed.</p>
        <Link to="/blogs" className="text-[#5C7A3A] font-semibold hover:underline inline-flex items-center gap-2">
          <ArrowLeft size={16} /> Back to Blog
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDF8F0]" data-testid="blog-detail-page">
      <SEO title={blog.title} description={blog.excerpt} image={blog.image} />

      {/* Hero Image */}
      <section className="relative h-[40vh] min-h-[320px] overflow-hidden">
        <div className="absolute inset-0">
          <img src={blog.image} alt={blog.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#2C1810] via-[#2C1810]/50 to-transparent" />
        </div>
        <div className="relative h-full max-w-4xl mx-auto px-4 flex flex-col justify-end pb-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <span className="inline-block bg-[#5C7A3A] text-white text-xs font-semibold px-3 py-1 rounded-full mb-4">{blog.category}</span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              {blog.title}
            </h1>
            <div className="flex items-center gap-4 text-white/70 text-sm">
              <span className="flex items-center gap-1.5"><User size={14} /> {blog.author}</span>
              <span className="flex items-center gap-1.5"><Calendar size={14} /> {formatDate(blog.created_at)}</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-3xl mx-auto px-4">
          <Link to="/blogs" className="inline-flex items-center gap-2 text-sm text-[#5C7A3A] font-medium hover:underline mb-8" data-testid="back-to-blogs">
            <ArrowLeft size={16} /> Back to Blog
          </Link>

          {/* Article body */}
          <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="prose prose-lg max-w-none"
            data-testid="blog-content"
          >
            <div
              className="text-[#2C1810] leading-relaxed space-y-4 blog-content"
              style={{ fontFamily: "'Inter', sans-serif" }}
              dangerouslySetInnerHTML={{ __html: formatContent(blog.content) }}
            />
          </motion.article>
        </div>
      </section>
    </div>
  );
};

function formatContent(content) {
  if (!content) return '<p>No content available.</p>';
  // Convert plain text paragraphs into HTML paragraphs
  // Support basic markdown-like formatting
  return content
    .split('\n\n')
    .map(para => {
      const trimmed = para.trim();
      if (!trimmed) return '';
      // Handle headers with ## syntax
      if (trimmed.startsWith('### ')) return `<h3 style="font-family: 'Playfair Display', serif; font-size: 1.25rem; font-weight: 700; color: #2C1810; margin-top: 2rem; margin-bottom: 0.5rem;">${trimmed.slice(4)}</h3>`;
      if (trimmed.startsWith('## ')) return `<h2 style="font-family: 'Playfair Display', serif; font-size: 1.5rem; font-weight: 700; color: #2C1810; margin-top: 2rem; margin-bottom: 0.5rem;">${trimmed.slice(3)}</h2>`;
      // Bold
      const formatted = trimmed.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      return `<p style="color: #4a3828; line-height: 1.8; margin-bottom: 1rem;">${formatted}</p>`;
    })
    .join('');
}

export default BlogDetail;
